import type { ReactNode } from 'react';
import { memo, useContext } from 'react';
import useStoreAndLanguageParams from '../../../../core/hooks/useStoreAndLanguageParams';
import useCategory from '../../../../core/queries/useCategory';
import PreorderModeContext from '../../../../preorder/PreorderModeContext';
import useSearchParamFilters from '../../../hooks/useSearchParamFilters';
import type { FilterList } from '../../../queries/types';
import type { ProductListQueryFilters } from './ProductListOptionsContext';
import ProductListOptionsContext from './ProductListOptionsContext';

const useFilters = () => {
    const { store, language } = useStoreAndLanguageParams();
    const { categoryId, rootCategoryId } = useCategory();
    const filters = useSearchParamFilters();
    return {
        ...filters,
        states: filters.states,
        filters: filters.filters,
        categoryId,
        store,
        language,
        rootCategoryId,
        pageSize: filters.pageSize || 24,
    };
};

export type ExternalFilters = Pick<
    ProductListQueryFilters,
    'vehicleId' | 'includeExcludedCategories'
> & {
    filters?: FilterList[];
};

const combineFilters = (
    filters: FilterList[],
    higherPriorityFilters: FilterList[]
) => {
    const remainingFilters = filters.filter(
        item => !higherPriorityFilters.find(({ name }) => name === item.name)
    );

    return [...higherPriorityFilters, ...remainingFilters];
};

const ProductListOptionsProvider = ({
    children,
    externalFilters,
    isFiltersLoading,
}: {
    children: ReactNode;
    externalFilters?: ExternalFilters;
    isFiltersLoading?: boolean;
}) => {
    const productListFilters = useFilters();
    const { productsFilters } = useContext(PreorderModeContext);

    return (
        <ProductListOptionsContext.Provider
            value={{
                isFiltersLoading,
                filters: {
                    ...productListFilters,
                    ...externalFilters,
                    ...productsFilters,
                    filters: combineFilters(
                        productListFilters.filters,
                        externalFilters?.filters ?? []
                    ),
                },
            }}
        >
            {children}
        </ProductListOptionsContext.Provider>
    );
};

export default memo(ProductListOptionsProvider);
